import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { Home, Building2, Droplets, ArrowRight, MapPin } from 'lucide-react';
import residentialCleaningImage from '../assets/axis-hero-cleaning.jpg';
import officeCleaningImage from '../assets/axis-office-cleaning.jpg';
import carpetCleaningImage from '../assets/axis-carpet-cleaning.jpeg';

const services = [
  {
    icon: Home,
    title: 'Residential Cleaning',
    subtitle: 'Detail-Clean Approach',
    description: 'Our service begins with a comprehensive "Detail-Clean" within the first two visits. On the first clean, we Detail-Clean your kitchen and bathrooms while thoroughly cleaning the rest. On the second clean, we Detail-Clean sleeping and living areas.',
    features: ['Weekly & Bi-Weekly Options', 'Move In / Move Out', 'Deep Cleaning', 'Airbnb Cleaning'],
    image: residentialCleaningImage,
    accent: 'from-axis-500 to-axis-600',
  },
  {
    icon: Building2,
    title: 'Commercial & Office',
    subtitle: 'Productivity-Focused',
    description: 'A clean office atmosphere directly contributes to productivity. Every commercial space is different, which is why we offer personalized cleaning plans tailored to your specific business needs.',
    features: ['Personalized Plans', 'Employee Satisfaction', 'Flexible Scheduling', 'Professional Results'],
    image: officeCleaningImage,
    accent: 'from-axis-600 to-axis-700',
  },
  {
    icon: Droplets,
    title: 'Specialty Services',
    subtitle: 'Beyond Standard Clean',
    description: 'From carpet cleaning to power washing and lawn care, we handle every outdoor and specialty cleaning challenge with professional expertise and attention to detail.',
    features: ['Carpet Cleaning', 'Power Washing', 'Lawn Services', 'Disinfection Services'],
    image: carpetCleaningImage,
    accent: 'from-sand-500 to-sand-600',
  },
];

const citiesServed = [
  'Gaithersburg',
  'Montgomery Village',
  'Rockville',
  'Derwood',
  'Olney',
  'Silver Spring',
  'Potomac',
  'North Bethesda',
  'Germantown',
  'Clarksburg',
  'Damascus',
  'Frederick',
];

function ServiceCard({ service, index }: { service: typeof services[0]; index: number }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 50 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
      transition={{ duration: 0.8, delay: index * 0.15, ease: [0.22, 1, 0.36, 1] }}
      className="group relative"
    >
      <div className="bg-white rounded-3xl overflow-hidden border border-axis-50 hover:shadow-2xl hover:shadow-axis-900/10 transition-all duration-500 hover:-translate-y-1">
        {/* Image */}
        <div className="relative h-56 overflow-hidden">
          <img
            src={service.image}
            alt={service.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
          />
          <div className={`absolute inset-0 bg-gradient-to-t ${service.accent} opacity-60`} />
          <div className="absolute bottom-4 left-4 right-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-10 h-10 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center border border-white/20">
                <service.icon className="w-5 h-5 text-white" />
              </div>
            </div>
            <h3 className="font-display text-2xl font-bold text-white">{service.title}</h3>
            <p className="text-sm text-white/80 font-medium">{service.subtitle}</p>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 lg:p-8">
          <p className="text-gray-600 leading-relaxed mb-6">{service.description}</p>

          <div className="grid grid-cols-2 gap-3 mb-6">
            {service.features.map((feature, i) => (
              <div key={i} className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-axis-400" />
                <span className="text-sm text-gray-700 font-medium">{feature}</span>
              </div>
            ))}
          </div>

          <a
            href="#contact"
            className="inline-flex items-center gap-2 text-axis-600 font-semibold text-sm group/link hover:text-axis-700 transition-colors"
          >
            Get a Quote
            <ArrowRight className="w-4 h-4 group-hover/link:translate-x-1 transition-transform" />
          </a>
        </div>
      </div>
    </motion.div>
  );
}

export default function Services() {
  const headerRef = useRef(null);
  const isHeaderInView = useInView(headerRef, { once: true, margin: '-100px' });

  return (
    <section id="services" className="relative py-24 sm:py-32 bg-white overflow-hidden">
      {/* Decorative */}
      <div className="absolute top-20 left-10 w-2 h-2 bg-axis-300 rounded-full" />
      <div className="absolute top-40 right-20 w-1.5 h-1.5 bg-sand-300 rounded-full" />
      <div className="absolute bottom-32 left-1/4 w-1 h-1 bg-axis-200 rounded-full" />

      <div className="max-w-7xl mx-auto section-padding">
        {/* Header */}
        <motion.div
          ref={headerRef}
          initial={{ opacity: 0, y: 40 }}
          animate={isHeaderInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="text-center max-w-2xl mx-auto mb-16"
        >
          <div className="inline-flex items-center gap-2 bg-axis-50 rounded-full px-4 py-1.5 mb-6">
            <span className="text-sm font-medium text-axis-700">What We Offer</span>
          </div>
          <h2 className="font-display text-4xl sm:text-5xl font-bold text-axis-950 leading-tight mb-4">
            Comprehensive
            <br />
            <span className="gradient-text italic">Cleaning Solutions</span>
          </h2>
          <p className="text-gray-500 text-lg">
            From residential detail-cleaning to commercial office maintenance, we deliver excellence in every service.
          </p>
        </motion.div>

        {/* Service Cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service, i) => (
            <ServiceCard key={i} service={service} index={i} />
          ))}
        </div>

        {/* Cities We Serve */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mt-20 text-center"
        >
          <h3 className="font-display text-2xl font-semibold text-axis-950 mb-2">Cities We Serve</h3>
          <p className="text-gray-500 mb-8">Proudly cleaning homes and offices throughout Montgomery County, MD.</p>
          <div className="flex flex-wrap justify-center gap-3 max-w-3xl mx-auto">
            {citiesServed.map((city) => (
              <span
                key={city}
                className="inline-flex items-center gap-1.5 bg-axis-50 text-axis-700 rounded-full px-4 py-2 text-sm font-medium"
              >
                <MapPin className="w-3.5 h-3.5" />
                {city}
              </span>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
